import { MessageBar, MessageBarType, TextField } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { Control, Controller, FieldErrors, useFieldArray, useFormContext } from "react-hook-form";

import { useDelayedState } from "../../hooks/use-delayed-state/useDelayedState";
import { generateId } from "../../utils/ids/generateId";
import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { IconButton } from "../inputs/button/IconButton";
import { TextFieldClearButton } from "../inputs/button/TextFieldClearButton";
import { Stack } from "../layouts/stack/Stack";
import { FormFailureMessages } from "./FormFailureMessages";

export type FormDynamicTextFieldProp = {
    name: string;
    label: string;
    control: Control;
    errors: FieldErrors;
    rules?: FormDynamicTextFieldRules;
    minItems?: number;
    requiredMessage?: string;
    multiline?: boolean;
    currentItems?: any[];
};

export type FormDynamicTextFieldRules = FormValidationRules<string>;

const formPropertyName = "value";

export interface FormDynamicTextFieldInput {
    [formPropertyName]: string;
}

export const FormDynamicTextField: React.FunctionComponent<FormDynamicTextFieldProp> = ({
    control,
    errors,
    label,
    name,
    rules,
    minItems,
    multiline,
    requiredMessage,
    currentItems
}: FormDynamicTextFieldProp) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const { fields, append, remove } = useFieldArray({
        control,
        name
    });

    const clearWarnings = () => {
        setWarnings([]);
    };

    useEffect(() => {
        if (!currentItems) {
            appendNewItem();

            return;
        }

        const [last] = currentItems.reverse();

        if (last && last[formPropertyName] === "") {
            return;
        }

        appendNewItem();
    }, []);

    const appendNewItem = () => {
        append({ value: "" });
    };

    const getItemInputFieldName = (index: number) => `${name}[${index}].${formPropertyName}`;

    const renderItemInputField = (
        renderProp: { value: string | undefined; onChange(value?: string): void },
        index: number,
        inputListLength: number
    ) => {
        const [delayedValue, inputValue, setInputValue] = useDelayedState(renderProp.value);

        useEffect(() => {
            renderProp.onChange(delayedValue);
        }, [delayedValue]);

        const handleInputChange = (
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
        ) => {
            if (newValue && inputListLength - 1 === index) {
                appendNewItem();
            }

            setInputValue(newValue);
        };

        const handleClearButton = () => {
            renderProp.onChange(undefined);
            clearWarnings();
        };

        const renderClearButton = () => (
            <TextFieldClearButton disabled={!renderProp.value} onClick={handleClearButton} />
        );

        const renderSuffix = () => (
            <Stack verticalAlign="center" horizontal={true}>
                {!!renderProp.value && renderClearButton()}
            </Stack>
        );

        return (
            <TextField
                data-testid={`${name}-input-${index}`}
                label={`${label} ${index + 1}`}
                name={getItemInputFieldName(index)}
                styles={{
                    suffix: { padding: 0, background: "transparent", alignItems: "start" },
                    fieldGroup: { border: errors[name]?.[index]?.["value"] && "2px solid red" },
                    root: { width: "100%" }
                }}
                onRenderSuffix={renderSuffix}
                onChange={handleInputChange}
                value={inputValue}
                multiline={multiline}
            />
        );
    };

    return (
        <>
            {fields.map((field, index, array) => (
                <Stack key={field.id} style={{ marginBottom: 10 }}>
                    <Stack style={{ flexDirection: "row" }}>
                        <Controller
                            name={getItemInputFieldName(index)}
                            control={control}
                            render={(renderProp) => renderItemInputField(renderProp, index, array.length)}
                            defaultValue={field.value}
                            rules={{
                                ...rules,
                                required: {
                                    value: !!minItems && index <= minItems - 1,
                                    message: requiredMessage ?? ""
                                },
                                validate: getValidate<string, FormDynamicTextFieldRules>(
                                    rules,
                                    control.formStateRef.current.submitCount,
                                    setWarnings
                                )
                            }}
                        />
                        {index !== 0 && (
                            <IconButton
                                iconProps={{ iconName: "Delete" }}
                                styles={{ tooltipHost: { root: { alignSelf: "flex-end" } } }}
                                onClick={() => remove(index)}
                            />
                        )}
                    </Stack>
                    <FormDynamicTextFieldErrorMessage name={name} index={index} errors={errors} />
                    <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} />
                </Stack>
            ))}
        </>
    );
};

interface FormDynamicTextFieldErrorMessageProp {
    errors: FieldErrors;
    index: number;
    name: string;
}

const FormDynamicTextFieldErrorMessage: React.FunctionComponent<FormDynamicTextFieldErrorMessageProp> = ({
    errors,
    index,
    name
}: FormDynamicTextFieldErrorMessageProp) => {
    const errorList = getErrors(errors, name, index);

    return (
        <>
            {errorList.map(({ id, message }) => (
                <MessageBar key={id} messageBarType={MessageBarType.error} isMultiline={true}>
                    {message}
                </MessageBar>
            ))}
        </>
    );
};

interface FormDynamicTextFieldError {
    id: string;
    message: string;
}

const getErrors = (errors: FieldErrors, name: string, index: number): FormDynamicTextFieldError[] => {
    const errorMessage = errors?.[name]?.[index]?.[formPropertyName]?.message;

    if (!errorMessage) {
        return [];
    }

    try {
        const failures = JSON.parse(errorMessage);

        if (Array.isArray(failures)) {
            return failures
                .filter((failure) => !!failure.message)
                .map((failure) => {
                    return {
                        id: generateId(),
                        message: failure.message
                    };
                });
        }
    } catch {
        return [{ id: generateId(), message: errorMessage }];
    }

    return [];
};
